import { useEffect, useLayoutEffect, useRef } from 'react';

import type {
  SearchPerformanceAnalyticsEventProperties,
  SearchPerformanceLoadTrigger,
} from './SearchPerformanceAnalyticsEvents';
import { useReportedRequests, type ReportedRequests } from './SearchPerformanceReportedRequests';
import { loadOutcome, type LoadFailure, type LoadOutcome, type RequestLoad } from './SearchPerformanceRequestLoad';
import useTrackSearchPerformanceEvent from './useTrackSearchPerformanceEvent';

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

type DataLoadedProperties = SearchPerformanceAnalyticsEventProperties['Search Performance: Data Loaded'];

type DataLoadFailedProperties = SearchPerformanceAnalyticsEventProperties['Search Performance: Data Load Failed'];

export type WidgetLoadedProperties = DistributiveOmit<DataLoadedProperties, 'duration_ms' | 'trigger'>;

export type WidgetLoadFailedProperties = DistributiveOmit<
  DataLoadFailedProperties,
  'status' | 'duration_ms' | 'trigger'
>;

export type WidgetCompanionLoad = {
  load: RequestLoad;
  failedProperties: WidgetLoadFailedProperties;
};

export type LoadScopeValue = string | number | boolean | null | undefined;

export type WidgetReportingWindow = {
  dateStart: string;
  dateEnd: string;
};

type WidgetLoad = {
  property: string | undefined;
  reportingWindow: WidgetReportingWindow;
  tabKey?: string;
  interactionQueryArgs?: readonly LoadScopeValue[];
  load: RequestLoad;
  loadedProperties: WidgetLoadedProperties;
  failedProperties: WidgetLoadFailedProperties;
  companion?: WidgetCompanionLoad;
};

export type WidgetLoadTelemetry = {
  trackRetryClicked: () => void;
};

type LoadScope = {
  property: string | undefined;
  reportingWindowKey: string;
  tabKey: string | undefined;
  interactionKey: string | undefined;
};

type SettledOutcome = Exclude<LoadOutcome, { outcome: 'pending' }>;

type RequestObservation = {
  requestId: string;
  trigger: SearchPerformanceLoadTrigger;
  settled: SettledOutcome | undefined;
  isFirstSight: boolean;
  settledOutOfView: boolean;
};

type RequestWatch = (load: RequestLoad, triggerNow: SearchPerformanceLoadTrigger) => RequestObservation | undefined;

type LoadReport<Properties> = {
  requestId: string;
  properties: Properties;
};

type LoadReports = {
  loaded: LoadReport<DataLoadedProperties> | undefined;
  failures: LoadReport<DataLoadFailedProperties>[];
};

type LoadReportsRequest = {
  widgetLoad: WidgetLoad;
  load: RequestObservation | undefined;
  companion: RequestObservation | undefined;
  reportedRequests: ReportedRequests;
  now: number;
};

function isSameScope(scope: LoadScope, other: LoadScope): boolean {
  return (
    scope.property === other.property &&
    scope.reportingWindowKey === other.reportingWindowKey &&
    scope.tabKey === other.tabKey &&
    scope.interactionKey === other.interactionKey
  );
}

function loadTrigger(
  scope: LoadScope,
  baseline: LoadScope,
  settledScope: LoadScope | undefined,
  isRetry: boolean
): SearchPerformanceLoadTrigger {
  if (isRetry) {
    return 'retry';
  }
  if (scope.property !== baseline.property) {
    return 'property_switch';
  }
  if (scope.reportingWindowKey !== baseline.reportingWindowKey) {
    return 'window_rollover';
  }
  if (scope.tabKey !== baseline.tabKey) {
    return 'tab_change';
  }
  if (scope.interactionKey !== baseline.interactionKey) {
    return 'interaction';
  }
  // Same scope as the baseline: the first request is the initial load; a later
  // request, once a load has already settled in this scope, is a refetch — the
  // toolbar Refresh, or a focus/reconnect refetch — reported as a refresh.
  return settledScope !== undefined && isSameScope(settledScope, scope) ? 'refresh' : 'initial';
}

function createRequestWatch(): RequestWatch {
  const seen = new Map<string, { trigger: SearchPerformanceLoadTrigger; wasInFlight: boolean }>();
  let lastRequestId: string | undefined;
  return (load, triggerNow) => {
    const { requestId } = load;
    if (requestId === undefined) {
      lastRequestId = undefined;
      return undefined;
    }
    const outcome = loadOutcome(load);
    const settled = outcome.outcome === 'pending' ? undefined : outcome;
    const isInFlight = settled === undefined;
    const known = seen.get(requestId);
    const watched = known ?? { trigger: triggerNow, wasInFlight: false };
    seen.set(requestId, { trigger: watched.trigger, wasInFlight: watched.wasInFlight || isInFlight });
    const isReturning = known !== undefined && requestId !== lastRequestId;
    lastRequestId = requestId;
    return {
      requestId,
      trigger: watched.trigger,
      settled,
      isFirstSight: known === undefined,
      settledOutOfView: isReturning && !isInFlight && watched.wasInFlight,
    };
  };
}

function responseStatus(error: unknown): number | string | undefined {
  return (error as { status?: number | string } | undefined)?.status;
}

function failureReport(
  failure: LoadFailure,
  failedProperties: WidgetLoadFailedProperties,
  trigger: SearchPerformanceLoadTrigger,
  now: number
): LoadReport<DataLoadFailedProperties> {
  return {
    requestId: failure.requestId,
    properties: {
      ...failedProperties,
      status: responseStatus(failure.error),
      duration_ms: now - failure.startedTimeStamp,
      trigger,
    },
  };
}

function latestTimeStamp(stamps: (number | undefined)[]): number | undefined {
  return stamps.every((stamp): stamp is number => stamp !== undefined) ? Math.max(...stamps) : undefined;
}

function loadAwaiting(load: RequestLoad, companion: RequestLoad | undefined): RequestLoad {
  if (companion === undefined || companion.isError) {
    return load;
  }
  return {
    ...load,
    fulfilledTimeStamp: latestTimeStamp([load.fulfilledTimeStamp, companion.fulfilledTimeStamp]),
    hasCurrentData: load.hasCurrentData && companion.hasCurrentData,
  };
}

function loadedReport(
  load: RequestObservation | undefined,
  outcome: LoadOutcome,
  loadedProperties: WidgetLoadedProperties,
  reportedRequests: ReportedRequests
): LoadReport<DataLoadedProperties> | undefined {
  if (load === undefined || outcome.outcome !== 'loaded' || reportedRequests.has(load.requestId)) {
    return undefined;
  }
  return {
    requestId: load.requestId,
    properties: { ...loadedProperties, duration_ms: outcome.durationMs, trigger: load.trigger },
  };
}

function failedReport(
  observation: RequestObservation | undefined,
  failedProperties: WidgetLoadFailedProperties | undefined,
  reportedRequests: ReportedRequests,
  now: number
): LoadReport<DataLoadFailedProperties> | undefined {
  if (observation?.settled?.outcome !== 'failed' || failedProperties === undefined) {
    return undefined;
  }
  return reportedRequests.has(observation.requestId)
    ? undefined
    : failureReport(observation.settled, failedProperties, observation.trigger, now);
}

function reportsFor({ widgetLoad, load, companion, reportedRequests, now }: LoadReportsRequest): LoadReports {
  const outcome = loadOutcome(loadAwaiting(widgetLoad.load, widgetLoad.companion?.load));
  return {
    loaded: loadedReport(load, outcome, widgetLoad.loadedProperties, reportedRequests),
    failures: [
      failedReport(load, widgetLoad.failedProperties, reportedRequests, now),
      failedReport(companion, widgetLoad.companion?.failedProperties, reportedRequests, now),
    ].filter((report): report is LoadReport<DataLoadFailedProperties> => report !== undefined),
  };
}

function emitReports(
  { loaded, failures }: LoadReports,
  reportedRequests: ReportedRequests,
  trackEvent: ReturnType<typeof useTrackSearchPerformanceEvent>
): void {
  if (loaded !== undefined) {
    reportedRequests.add(loaded.requestId);
    trackEvent('Search Performance: Data Loaded', loaded.properties);
  }
  failures.forEach(({ requestId, properties }) => {
    reportedRequests.add(requestId);
    trackEvent('Search Performance: Data Load Failed', properties);
  });
}

function useWidgetLoadTelemetry(widgetLoad: WidgetLoad): WidgetLoadTelemetry {
  const { property, reportingWindow, tabKey, interactionQueryArgs, load, companion } = widgetLoad;
  const widget = widgetLoad.loadedProperties.widget;
  const trackEvent = useTrackSearchPerformanceEvent();
  const reportedRequests = useReportedRequests();
  const latestRef = useRef(widgetLoad);
  useLayoutEffect(() => {
    latestRef.current = widgetLoad;
  });
  const baselineRef = useRef<LoadScope | undefined>(undefined);
  const settledScopeRef = useRef<LoadScope | undefined>(undefined);
  const retryScopeRef = useRef<LoadScope | undefined>(undefined);
  const watchLoadRef = useRef<RequestWatch>(createRequestWatch());
  const watchCompanionRef = useRef<RequestWatch>(createRequestWatch());
  const reportingWindowKey = `${reportingWindow.dateStart}/${reportingWindow.dateEnd}`;
  const interactionKey = interactionQueryArgs === undefined ? undefined : JSON.stringify(interactionQueryArgs);
  const scope: LoadScope = { property, reportingWindowKey, tabKey, interactionKey };

  useEffect(() => {
    const currentScope: LoadScope = { property, reportingWindowKey, tabKey, interactionKey };
    if (retryScopeRef.current !== undefined && !isSameScope(retryScopeRef.current, currentScope)) {
      retryScopeRef.current = undefined;
    }
    if (baselineRef.current === undefined && property !== undefined) {
      baselineRef.current = currentScope;
    }
    const latest = latestRef.current;
    const triggerNow = loadTrigger(
      currentScope,
      baselineRef.current ?? currentScope,
      settledScopeRef.current,
      retryScopeRef.current !== undefined
    );
    const observedLoad = watchLoadRef.current(latest.load, triggerNow);
    // The companion (columns) inherits the trigger of the listing that asked for
    // it, not of whatever render it first appears in — it can arrive a render
    // after the listing settled, when `triggerNow` has already moved to refresh.
    const observedCompanion =
      latest.companion && watchCompanionRef.current(latest.companion.load, observedLoad?.trigger ?? triggerNow);
    if (observedLoad?.isFirstSight || observedCompanion?.isFirstSight) {
      retryScopeRef.current = undefined;
    }
    [observedLoad, observedCompanion].forEach((observation) => {
      if (observation?.settledOutOfView) reportedRequests.add(observation.requestId);
    });
    if (observedLoad?.settled !== undefined) {
      baselineRef.current = currentScope;
      settledScopeRef.current = currentScope;
    }

    emitReports(
      reportsFor({ widgetLoad: latest, load: observedLoad, companion: observedCompanion, reportedRequests, now: Date.now() }),
      reportedRequests,
      trackEvent
    );
  }, [
    property,
    reportingWindowKey,
    tabKey,
    interactionKey,
    load.requestId,
    load.hasCurrentData,
    load.isError,
    companion?.load.requestId,
    companion?.load.hasCurrentData,
    companion?.load.isError,
    reportedRequests,
    trackEvent,
  ]);

  const trackRetryClicked = () => {
    retryScopeRef.current = scope;
    trackEvent('Search Performance: Retry Clicked', { widget });
  };

  return { trackRetryClicked };
}

export default useWidgetLoadTelemetry;
